<template>
  <div class="video-container">
    <div ref="player"></div>
  </div>
</template>

<script>
export default {
  props: {
    videoId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      player: null,
      videoDuration: 0,
      watchIntervals: new Set(),
      checkInterval: null,
      totalIntervals: null,
      completedPercentage: 0,
      lastEmittedInterval: null,
      saveDebounce: null,
    };
  },
  created() {
  },
  methods: {
    createPlayer() {
      if (this.player) return; // already created
      try {
        this.player = new YT.Player(this.$refs.player, {
          width: '100%',
          videoId: this.videoId,
          events: {
            onReady: this.onPlayerReady,
            onStateChange: this.onPlayerStateChange
          }
        });
      } catch (e) {
        // swallow until API ready
      }
    },
    onPlayerReady(event) {
      this.videoDuration = event.target.getDuration();
      this.totalIntervals = Math.floor(this.videoDuration / 10);
      this.checkInterval = setInterval(this.trackWatchProgress, 1000);
      // restore saved progress if any
      const saved = window.localStorage.getItem(this.localStorageKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
            if (parsed.videoId === this.videoId && Array.isArray(parsed.watched)) {
              parsed.watched.forEach(i => this.watchIntervals.add(i));
              this.$emit('update-view', { watched: [...this.watchIntervals], total: this.totalIntervals });
            }
        } catch(_){}
      }
    },
    onPlayerStateChange(event) {
      if (event.data === YT.PlayerState.PAUSED || event.data === YT.PlayerState.ENDED) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
        // final save on pause/end
        this.persistProgress();
      } else if (event.data === YT.PlayerState.PLAYING) {
        // avoid stacking intervals
        if (this.checkInterval) clearInterval(this.checkInterval);
        this.checkInterval = setInterval(this.trackWatchProgress, 1000);
      }
    },
    trackWatchProgress() {
      if (!this.player || typeof this.player.getCurrentTime !== 'function') return;
      const currentTime = this.player.getCurrentTime();
      const currentInterval = Math.floor(currentTime / 10);
      if (currentInterval !== this.lastEmittedInterval) {
        this.lastEmittedInterval = currentInterval;
        if (!this.watchIntervals.has(currentInterval)) {
          this.watchIntervals.add(currentInterval);
          this.completedPercentage = Math.ceil((this.watchIntervals.size / this.totalIntervals) * 100);
          this.$emit('update-view', { watched: [...this.watchIntervals], total: this.totalIntervals });
          this.persistProgress();
        }
      }
    }
  ,
    persistProgress() {
      // debounce writes to localStorage
      if (this.saveDebounce) clearTimeout(this.saveDebounce);
      this.saveDebounce = setTimeout(() => {
        try {
          window.localStorage.setItem(this.localStorageKey, JSON.stringify({
            videoId: this.videoId,
            watched: [...this.watchIntervals],
            total: this.totalIntervals
          }));
        } catch(_) {}
      }, 300);
    }
  },
  mounted() {
    // compute key once
    this.localStorageKey = `videoProgress_${this.videoId}`;
    const ensurePlayer = () => this.createPlayer();
    if (window.YT && window.YT.Player) {
      ensurePlayer();
    } else {
      // queue callbacks until API ready
      window._ytReadyQueue = window._ytReadyQueue || [];
      window._ytReadyQueue.push(ensurePlayer);
      if (!window.onYouTubeIframeAPIReady) {
        window.onYouTubeIframeAPIReady = () => {
          (window._ytReadyQueue || []).forEach(cb => { try { cb(); } catch(_){} });
          window._ytReadyQueue = [];
        };
      }
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    }
  },
  beforeUnmount() {
    if (this.checkInterval) clearInterval(this.checkInterval);
    this.persistProgress();
  }
};
</script>

<style scoped>
div {
  text-align: center;
}
.video-container {
    position: relative;
    width: 100%;
    padding-bottom: 56.25%; /* 16:9 aspect ratio */
    height: 0;
    overflow: hidden;
    margin-bottom: 12px;
}
</style>
